/**
 * Ключи кэша Переписок и правка кэша из событий / оптимистичной отправки.
 * Единственное место, где знают формат ключей.
 */
import type { QueryClient } from '@tanstack/react-query';
import type { InboxConversation } from '@/api/inboxTypes';
import { applyPatch, type ConvPatch, type ListData } from './convList';
import type { ListQuery } from './inboxQuery';
import type { MessagesState } from './messageStore';

export const cwKeys = {
  all: ['cw'] as const,
  profile: () => ['cw', 'profile'] as const,
  inboxes: (acc: number) => ['cw', 'inboxes', acc] as const,
  stages: (acc: number, inboxId: number) => ['cw', 'stages', acc, inboxId] as const,
  /** Правила канала (unia_capabilities); conv — только для страницы Facebook (Instagram Direct), иначе 0. */
  caps: (acc: number, inboxId: number, conv: number) => ['cw', 'caps', acc, inboxId, conv] as const,
  unread: (acc: number) => ['cw', 'unread', acc] as const,
  /** Все списки кабинета (по префиксу). */
  convsOf: (acc: number) => ['cw', 'convs', acc] as const,
  convs: (acc: number, q: ListQuery) => ['cw', 'convs', acc, q] as const,
  meta: (acc: number, q: ListQuery) => ['cw', 'meta', acc, q] as const,
  conv: (acc: number, id: number) => ['cw', 'conv', acc, id] as const,
  msgs: (acc: number, id: number) => ['cw', 'msgs', acc, id] as const,
};

export interface PatchSummary {
  /** Переписка есть хотя бы в одном кэше списка. */
  found: boolean;
  /** Списков в кэше вообще нет — дозагружать нечего, откроется свежим запросом. */
  anyList: boolean;
  unknown: boolean;
}

/** Применить патч ко ВСЕМ спискам кабинета в кэше (включая скрытые запросы для значка вкладки). */
export function patchAllLists(qc: QueryClient, acc: number, patch: ConvPatch, myUserId: number | null): PatchSummary {
  const entries = qc.getQueriesData<ListData>({ queryKey: cwKeys.convsOf(acc) });
  let found = false;
  let unknown = false;
  let anyList = false;
  for (const [key, data] of entries) {
    if (!data) continue;
    anyList = true;
    const query = key[3] as ListQuery;
    const res = applyPatch(data, patch, query, myUserId);
    found ||= res.found;
    unknown ||= res.unknown;
    if (res.changed) qc.setQueryData<ListData>(key, res.data);
  }
  // «Неизвестной» переписка считается, только если её нет ни в одном списке.
  return { found, anyList, unknown: unknown && !found };
}

/** Записать снимок переписки: в списки и в отдельный кэш открытой переписки. */
export function putConversation(qc: QueryClient, acc: number, conv: InboxConversation, myUserId: number | null): void {
  patchAllLists(qc, acc, { kind: 'snapshot', conv }, myUserId);
  qc.setQueryData<InboxConversation>(cwKeys.conv(acc, conv.id), (old) =>
    old ? { ...conv, lastMessage: conv.lastMessage ?? old.lastMessage } : conv);
}

export function updateMessages(
  qc: QueryClient,
  acc: number,
  convId: number,
  fn: (s: MessagesState | undefined) => MessagesState | undefined,
): void {
  const key = cwKeys.msgs(acc, convId);
  const next = fn(qc.getQueryData<MessagesState>(key));
  if (next) qc.setQueryData<MessagesState>(key, next);
}

/** Найти переписку в любом из закэшированных списков (для мгновенного показа при открытии). */
export function findConversation(qc: QueryClient, acc: number, id: number): InboxConversation | undefined {
  for (const [, data] of qc.getQueriesData<ListData>({ queryKey: cwKeys.convsOf(acc) })) {
    for (const page of data?.pages ?? []) {
      const hit = page.conversations.find((c) => c.id === id);
      if (hit) return hit;
    }
  }
  return undefined;
}
